import React from 'react';

import { FCWithChildren } from '@wix/challenges-web-library';
import utils, {
  ChallengeEntityType,
  isStepAvailableForComplete,
  isStepOverdue,
  isStepResolved,
} from '../../../../utils';
import { useResolveStep } from '../../../../../../../../contexts/ResolveStep/ResolveStepContext';
import { useSettings } from '@wix/tpa-settings/react';
import { useSections } from '../../../../../../../../contexts/ParticipantSections/ParticipantSectionsContext';
import { useLoading } from '../../../../../../../../contexts/Loading/LoadingContext';
import {
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import {
  ButtonPriority as ButtonPRIORITY,
  ButtonSize,
  ButtonSize as ButtonSIZE,
} from '@wix/wix-ui-tpa/cssVars';
import {
  ChevronLeftSmall,
  ChevronRightSmall,
} from '@wix/wix-ui-icons-common/on-stage';
import { sidebarLayoutDataHooks } from '../../../sidebarLayoutDataHooks';
import { useStepIsUnavailable } from '../utils/useStepIsUnavailable';
import { useSidebarLayoutBase } from '../../../contexts/SidebarLayoutBase/SidebarLayoutBaseContext';
import { useVideoStepData } from '../../../../../../../../hooks/useVideoStepData';
import { isUserCompleted } from '../../../../../../../../contexts/User/helpers/userTypeHandlers';
import { useUser } from '../../../../../../../../contexts/User/UserContext';
import { PreviewActionBlockerContainer } from '../../../../../../../../components-shared/ActionBlocker/PreviewActionBlockerContainer';
import { ActionBlockerAppearance } from '../../../../../../../../components-shared/ActionBlocker/ActionBlocker';
import { SidebarLayoutButton } from '../../../../../components/Buttons/SidebarLayoutButton/SidebarLayoutButton';
import { StepUndo } from './components/StepUndo';
import challengeSettings from '../../../../../../settingsParams';
import { IControl } from '../utils/types';
import { Challenges } from '../../../../../../../../editor/types/Experiments';
import {
  FocusPoint,
  useFocusManager,
} from '../../../../../../../../contexts/FocusManager';
import { ParticipantStep } from '../../../../../../../../types/v3Types';
import { useCSSPBEnabled } from '../../../../../../../../hooks/useCSSPBStyle';
import { classes, st } from './StepControls.st.css';
import TextWithDirection from '../../../../../../../../components-shared/TextWithDirection';
import { getFormattedDate } from '../../../../../components/StepsListAsTiles/GetFormattedDate';

export interface IStepControls extends IControl {
  completeBtnDataHook?: string;
  nextBtnDataHook?: string;
  currentStep: ParticipantStep;
  buttonState: string;
  onStepResolve(
    currentStep: ParticipantStep,
    options?: { background?: boolean },
  ): Promise<void>;
}

export const StepControlsV2: FCWithChildren<IStepControls> = (props) => {
  const { t } = useTranslation();
  const { isRTL, isMobile, language } = useEnvironment();

  const settings = useSettings();
  const { participant } = useUser();
  const { isResolveStepRequestInProgress } = useResolveStep();
  const stepId = props.currentStep?.id;
  const isCompletedChallenge = isUserCompleted(
    participant?.transitions?.[0]?.state,
  );
  const cssPBEnabled = useCSSPBEnabled({
    includeParticipantPageAlignmentCheck: true,
  });

  const {
    isStepUnavailable,
    isLockedButVisible,
    isFutureScheduledStep,
    firstAvailableStep,
    isSectionLocked,
  } = useStepIsUnavailable();
  const { stepDescriptionLoaded } = useSections();
  const { hasRicosLoaded } = useLoading();
  const { setNextFocusPoint } = useFocusManager();

  const previousEntity = utils.getFollowingEntity(
    props.currentStep?.id,
    {
      listParticipantSections: props.sections,
      participantSteps: props.steps,
    },
    'previous',
  );

  const nextEntity = utils.getFollowingEntity(props.currentStep?.id, {
    listParticipantSections: props.sections,
    participantSteps: props.steps,
  });

  const isPreviousButtonShown =
    previousEntity.type !== ChallengeEntityType.VOID;

  const stepResolved = isStepResolved(props.currentStep);
  const isFinalStep = nextEntity.type === ChallengeEntityType.VOID;

  const isCompleteButtonShown =
    !isCompletedChallenge &&
    !isSectionLocked &&
    ((!isStepUnavailable &&
      (isStepAvailableForComplete(props.currentStep) ||
        isStepOverdue(props.currentStep))) ||
      (stepResolved && !isFinalStep) ||
      isLockedButVisible ||
      isFutureScheduledStep);

  const isUndoButtonShown = !isCompletedChallenge && !isStepUnavailable;

  let completeButtonLabel = settings.get(
    challengeSettings.completeButtonTxt,
  ) as string;
  if (stepResolved) {
    completeButtonLabel = settings.get(
      challengeSettings.nextButtonTxt,
    ) as string;
  } else if (isLockedButVisible || isFutureScheduledStep) {
    completeButtonLabel = t('challenge.page.steps.go-to-required-step');
  }

  const showCompleteButtonArrow = !isFinalStep;

  const { isVideo } = useVideoStepData(props.currentStep?.source);
  const { videoStepAccepted, isVideoLoadingError } = useSidebarLayoutBase();
  const [isVideoNotificationVisible, setIsVideoNotificationVisible] =
    React.useState<boolean>(false);

  const { submitStep } = useCompleteStep({
    currentStep: props.currentStep,
    nextEntity,
    onStepResolve: props.onStepResolve,
    onNextEntity: props.onNextEntity,
  });

  React.useEffect(() => {
    setIsVideoNotificationVisible(false);
  }, [stepId, videoStepAccepted]);

  const shouldHideButton =
    !isStepUnavailable && (!hasRicosLoaded || !stepDescriptionLoaded(stepId));

  const CompleteButton =
    isCompleteButtonShown && !shouldHideButton ? (
      <PreviewActionBlockerContainer
        appearance={ActionBlockerAppearance.PopoverAppearance}
        content={completeButtonLabel}
        isActionAllowed={false}
      >
        <SidebarLayoutButton
          className={classes.stepViewMainCTA}
          dataHook={props.completeBtnDataHook || null}
          disabled={isResolveStepRequestInProgress || isVideoLoadingError}
          fullWidth={isMobile}
          size={isMobile ? ButtonSIZE.small : ButtonSize.medium}
          suffixIcon={showCompleteButtonArrow ? <ChevronRightSmall className={classes.ltrArrow} /> : null}
          prefixIcon={showCompleteButtonArrow ? <ChevronLeftSmall className={classes.rtlArrow} /> : null}
          onClick={handleCompleteButtonClick}
        >
          {completeButtonLabel}
        </SidebarLayoutButton>
      </PreviewActionBlockerContainer>
    ) : null;

  function handleCompleteButtonClick() {
    if (isLockedButVisible || isFutureScheduledStep) {
      setNextFocusPoint(FocusPoint.Top);
      props.onNextEntity({
        step: firstAvailableStep,
        type: ChallengeEntityType.STEP,
        id: firstAvailableStep?.id,
      });
      return;
    }
    if (stepResolved) {
      setNextFocusPoint(FocusPoint.Top);
      props.onNextEntity(nextEntity);
      return;
    }
    setNextFocusPoint(FocusPoint.Bottom);
    if (isVideo && !videoStepAccepted) {
      setIsVideoNotificationVisible(true);
      return;
    }
    submitStep();
  }

  const PreviousButton =
    isPreviousButtonShown && !shouldHideButton ? (
      <SidebarLayoutButton
        dataHook={sidebarLayoutDataHooks.stepPreviousBtn()}
        className={classes.previousButton}
        withDefaultTheme={true}
        priority={ButtonPRIORITY.secondary}
        disabled={isResolveStepRequestInProgress}
        fullWidth={isMobile}
        size={isMobile ? ButtonSIZE.small : ButtonSIZE.medium}
        prefixIcon={<ChevronLeftSmall className={classes.ltrArrow} />}
        suffixIcon={<ChevronRightSmall className={classes.rtlArrow} />}
        moveFocus={false}
        onClick={async () => {
          setNextFocusPoint(FocusPoint.Top);
          await props.onNextEntity(previousEntity);
        }}
      >
        {settings.get(challengeSettings.prevButtonTxt) as string}
      </SidebarLayoutButton>
    ) : null;

  const UndoButton =
    isUndoButtonShown && !shouldHideButton ? (
      <PreviewActionBlockerContainer
        appearance={ActionBlockerAppearance.PopoverAppearance}
        content={t('action-blocker.preview-mode.complete-step')}
        isActionAllowed={false}
      >
        <StepUndo>
          {settings.get(challengeSettings.undoButtonTxt) as string}
        </StepUndo>
      </PreviewActionBlockerContainer>
    ) : null;

  const textMessage = getTextMessage();

  function getTextMessage() {
    // Watch the video to complete this step
    if (isVideoNotificationVisible) {
      return t('challenge.page.step-video-required.warning');
    }

    // You’ll be able to complete this step on Mon, Mar 16, 2026
    if (isFutureScheduledStep) {
      return t('live.challenges-page.hidden-step.description-scheduled', {
        date: getFormattedDate(language, props.currentStep?.dateFrame?.start),
      });
    }

    // This step is locked. Complete the required step first. ?
    if (isLockedButVisible) {
      return t('challenge.page.steps.locked-required-step-warning');
    }

    return null;
  }

  return (
    <div
      className={st(classes.root, {
        mobile: isMobile,
        align: !cssPBEnabled
          ? settings.get(challengeSettings.sidebarLayoutTextAlignment)
          : null,
        withCSSAlignment: cssPBEnabled,
        rtl: isRTL,
        buttonState: props.buttonState,
        stepResolved,
      })}
      role="region"
      aria-live="polite"
    >
       <div className={classes.secondaryControls}>
        {PreviousButton}
        {isMobile && UndoButton}
       </div>
       {textMessage ? (
        <p className={classes.textMessage}>
          <TextWithDirection>{textMessage}</TextWithDirection>
        </p>
       ) : null}
       <div className={classes.mainControls}>
        {!isMobile && UndoButton}
        {CompleteButton}
       </div>
    </div>
  );
};

interface UseCompleteStepParams {
  currentStep: ParticipantStep;
  nextEntity: ReturnType<typeof utils.getFollowingEntity>;
  onStepResolve(
    currentStep: ParticipantStep,
    options?: { background?: boolean },
  ): Promise<void>;
  onNextEntity: IControl['onNextEntity'];
}

function useCompleteStep({
  currentStep,
  nextEntity,
  onStepResolve,
  onNextEntity,
}: UseCompleteStepParams) {
  const { experiments } = useExperiments();

  const isUniteNextAndCompleteEnabled = experiments.enabled(
    Challenges.uniteNextAndCompleteButton,
  );

  const { isResolveStepRequestInProgress } = useResolveStep();

  function submitStep() {
    if (isResolveStepRequestInProgress) {
      return;
    }

    if (isUniteNextAndCompleteEnabled) {
      // Order matters: navigate BEFORE kicking off the resolve request so the
      // user doesn't briefly see Undo/Next render in a post-complete state on
      // the step they just left.
      //
      // This works because `navigateToStep` in SidebarLayoutForParticipant
      // bails when `isResolveStepRequestInProgress` is true, and the flag is
      // flipped inside `resolveStep` on a microtask after an `await`. We call
      // `onNextEntity` synchronously first, so the flag is still false when
      // navigation runs. `background: true` then suppresses the flag entirely
      // for this request, so the buttons on the next step don't flash
      // disabled for an in-flight resolve the user has already moved past.
      onNextEntity(nextEntity);
      onStepResolve(currentStep, { background: true });
    } else {
      onStepResolve(currentStep);
    }
  }

  return { submitStep };
}
